import { useState, useEffect, useRef, useCallback } from 'react';
import { MapLibreMap, MapMarker } from '@molecules/index';
import { Button, Input, Card, Icon, Select } from '@atoms/index';
import { addressService, CreateAddressDTO } from '@services/addressService';
import { geocodingService, type GeocodingResult } from '@services/geocodingService';
import { TURKISH_CITY_OPTIONS, matchTurkishCity } from '@/data/turkish-cities';
import type { Map } from 'maplibre-gl';
import type { UserAddress } from '@/types';

interface AddAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialAddress?: UserAddress | null;
}

const DEFAULT_CENTER: [number, number] = [35.3308, 36.9914];

export const AddAddressModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialAddress = null,
}: AddAddressModalProps) => {
  const isEditMode = Boolean(initialAddress?.id);
  const [mapPosition, setMapPosition] = useState<[number, number] | null>(null);
  const [mapInstance, setMapInstance] = useState<Map | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<CreateAddressDTO>({
    title: '',
    city: '',
    district: '',
    fullAddress: '',
    latitude: 0,
    longitude: 0,
    buildingInfo: '',
    isDefault: false,
  });

  useEffect(() => {
    if (!isOpen) {
      setMapPosition(null);
      setError(null);
      setIsMapReady(false);
      setSearchQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      setFormData({
        title: '',
        city: '',
        district: '',
        fullAddress: '',
        latitude: 0,
        longitude: 0,
        buildingInfo: '',
        isDefault: false,
      });
    } else if (isOpen) {
      if (initialAddress) {
        const rawLoc = initialAddress.location;
        const locObj = typeof rawLoc === 'string' ? (() => { try { return JSON.parse(rawLoc); } catch { return null; } })() : rawLoc;
        const coords = locObj?.coordinates;
        const center: [number, number] = Array.isArray(coords) && coords.length >= 2 ? [Number(coords[0]), Number(coords[1])] : DEFAULT_CENTER;
        setMapPosition(center);
        setFormData({
          title: initialAddress.title,
          city: initialAddress.city,
          district: initialAddress.district,
          fullAddress: initialAddress.fullAddress,
          latitude: coords ? Number(coords[1]) : 0,
          longitude: coords ? Number(coords[0]) : 0,
          buildingInfo: initialAddress.buildingInfo || '',
          isDefault: initialAddress.isDefault ?? false,
        });
        setSearchQuery(initialAddress.fullAddress);
      } else if (!mapPosition) {
        setMapPosition(DEFAULT_CENTER);
      }
      setTimeout(() => setIsMapReady(true), 100);
    }
  }, [isOpen, initialAddress]);

  useEffect(() => {
    if (mapPosition) {
      setFormData((prev) => ({
        ...prev,
        latitude: mapPosition[1],
        longitude: mapPosition[0],
      }));
    }
  }, [mapPosition]);

  const reverseGeocode = useCallback(async (latitude: number, longitude: number) => {
    setIsReverseGeocoding(true);
    try {
      const result = await geocodingService.reverseGeocode(latitude, longitude);
      
      if (result) {
        let finalDistrict = result.district;
        
        if (!finalDistrict && result.fullAddress) {
          const addressParts = result.fullAddress.split(',').map(s => s.trim());
          const turkishDistrictKeywords = ['Mahallesi', 'Mahalle', 'İlçe', 'ilçe'];
          
          for (const part of addressParts) {
            if (part && part !== result.city && part !== 'Türkiye' && part !== 'Turkey') {
              const postcodeDistrictMatch = part.match(/^\d{5}\s+([^/]+)\/(.+)$/);
              if (postcodeDistrictMatch) {
                finalDistrict = postcodeDistrictMatch[1].trim();
                break;
              }
              
              const districtCityMatch = part.match(/^([^/]+)\/(.+)$/);
              if (districtCityMatch && !part.match(/^\d{5}/)) {
                finalDistrict = districtCityMatch[1].trim();
                break;
              }
              
              if (turkishDistrictKeywords.some(keyword => part.includes(keyword))) {
                finalDistrict = part.replace(/Mahallesi|Mahalle|İlçe|ilçe/g, '').trim();
                break;
              }
              
              if (!part.match(/^\d{5}/) && !part.includes('Sk.') && !part.includes('Sokak') && !part.includes('Cadde') && !part.includes('Bulvar') && !part.includes('No:')) {
                if (part.length > 2 && part.length < 30) {
                  finalDistrict = part;
                  break;
                }
              }
            }
          }
        }
        
        console.log('Reverse geocode - Final district:', finalDistrict);
        
        setFormData((prev) => ({
          ...prev,
          city: matchTurkishCity(result.city) || prev.city || '',
          district: finalDistrict || prev.district || '',
          fullAddress: result.fullAddress || prev.fullAddress || '',
        }));
        
        setSearchQuery(result.displayName);
      }
    } catch (err) {
      console.error('Reverse geocoding error:', err);
    } finally {
      setIsReverseGeocoding(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await geocodingService.searchAddresses(query);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (err: any) {
      console.error('Suggestions error:', err);
      setSuggestions([]);
      setShowSuggestions(false);
      
      if (err?.response?.status === 500) {
        const errorMessage = err?.response?.data?.error?.message || '';
        if (errorMessage.includes('API key not configured') || errorMessage.includes('access token not configured')) {
          setError('Adres arama servisi yapılandırılmamış. Lütfen yöneticiye bildirin.');
        }
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (searchQuery.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(searchQuery);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchSuggestions]);

  const handleAddressSelect = async (selectedSuggestion: GeocodingResult) => {
    setError(null);
    setShowSuggestions(false);
    setIsSearching(true);

    try {
      const { latitude, longitude, city, district, fullAddress, displayName } = selectedSuggestion;
      
      console.log('Selected address data:', { city, district, fullAddress, displayName });
      
      setMapPosition([longitude, latitude]);
      
      let finalDistrict = district;
      
      if (!finalDistrict && fullAddress) {
        const addressParts = fullAddress.split(',').map(s => s.trim());
        const turkishDistrictKeywords = ['Mahallesi', 'Mahalle', 'İlçe', 'ilçe'];
        
        for (const part of addressParts) {
          if (part && part !== city && part !== 'Türkiye' && part !== 'Turkey') {
            const postcodeDistrictMatch = part.match(/^\d{5}\s+([^/]+)\/(.+)$/);
            if (postcodeDistrictMatch) {
              finalDistrict = postcodeDistrictMatch[1].trim();
              break;
            }
            
            const districtCityMatch = part.match(/^([^/]+)\/(.+)$/);
            if (districtCityMatch && !part.match(/^\d{5}/)) {
              finalDistrict = districtCityMatch[1].trim();
              break;
            }
            
            if (turkishDistrictKeywords.some(keyword => part.includes(keyword))) {
              finalDistrict = part.replace(/Mahallesi|Mahalle|İlçe|ilçe/g, '').trim();
              break;
            }
            
            if (!part.match(/^\d{5}/) && !part.includes('Sk.') && !part.includes('Sokak') && !part.includes('Cadde') && !part.includes('Bulvar') && !part.includes('No:')) {
              if (part.length > 2 && part.length < 30) {
                finalDistrict = part;
                break;
              }
            }
          }
        }
      }
      
      console.log('Final district after parsing:', finalDistrict);
      
      setFormData((prev) => ({
        ...prev,
        latitude,
        longitude,
        city: matchTurkishCity(city) || prev.city || '',
        district: finalDistrict || prev.district || '',
        fullAddress: fullAddress || displayName || prev.fullAddress || '',
      }));
      
      if (!finalDistrict && city) {
        console.warn('District not found in geocoding result, user may need to fill manually');
      }
      
      setSearchQuery(displayName);

      if (mapInstance) {
        mapInstance.flyTo({
          center: [longitude, latitude],
          zoom: 15,
          duration: 1000,
        });
      }
    } catch (err) {
      console.error('Address select error:', err);
      setError('Adres seçilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setError('Lütfen en az 3 karakter girin');
      return;
    }

    setIsSearching(true);
    setError(null);
    setShowSuggestions(false);

    try {
      const results = await geocodingService.searchAddresses(searchQuery);
      
      if (results.length === 0) {
        setError('Aradığınız adres bulunamadı. Lütfen farklı bir arama yapın veya haritada konum seçin.');
        return;
      }

      if (results.length === 1) {
        await handleAddressSelect(results[0]);
      } else {
        setSuggestions(results);
        setShowSuggestions(true);
      }
    } catch (err: any) {
      console.error('Geocoding error:', err);
      
      if (err?.response?.status === 500) {
        const errorMessage = err?.response?.data?.error?.message || '';
        if (errorMessage.includes('API key not configured') || errorMessage.includes('access token not configured')) {
          setError('Adres arama servisi yapılandırılmamış. Lütfen yöneticiye bildirin veya haritada konum seçin.');
        } else {
          setError('Adres arama sırasında bir hata oluştu. Lütfen tekrar deneyin.');
        }
      } else {
        setError('Adres arama sırasında bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleMapClick = useCallback((lng: number, lat: number) => {
    setMapPosition([lng, lat]);
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  const handleMapLoad = useCallback((map: Map) => {
    setMapInstance(map);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.title || !formData.city || !formData.district || !formData.fullAddress) {
      setError('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    if (!mapPosition) {
      setError('Lütfen haritada bir konum seçin');
      return;
    }

    setIsSubmitting(true);

    try {
      const rawLat = mapPosition[1];
      const rawLng = mapPosition[0];

      if (isNaN(rawLat) || isNaN(rawLng)) {
        setError('Geçersiz konum koordinatları. Lütfen haritada bir konum seçin.');
        setIsSubmitting(false);
        return;
      }

      const latitude = parseFloat(rawLat.toFixed(8));
      const longitude = parseFloat(rawLng.toFixed(8));

      const payload = {
        title: formData.title,
        city: formData.city,
        district: formData.district,
        fullAddress: formData.fullAddress,
        latitude,
        longitude,
        buildingInfo: formData.buildingInfo || undefined,
        isDefault: formData.isDefault || false,
      };
      if (isEditMode && initialAddress?.id) {
        await addressService.updateAddress(initialAddress.id, payload);
      } else {
        await addressService.createAddress(payload);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const rawMessage = err.response?.data?.error?.message;
      const displayMessage = Array.isArray(rawMessage)
        ? rawMessage[0]
        : rawMessage || 'Adres kaydedilirken bir hata oluştu.';
      setError(displayMessage);
      console.error('Create address error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <Card className="w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isEditMode ? 'Adres bilgilerini güncelleyin' : 'Haritada konumunuzu seçin veya adres bilgilerinizi girin'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Konum Seçimi *
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Haritada tıklayarak konum seçin veya adres arayın
                </p>
                
                <div className="relative mb-3">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                          }}
                          onFocus={() => {
                            if (suggestions.length > 0) {
                              setShowSuggestions(true);
                            }
                          }}
                          onBlur={() => {
                            setTimeout(() => setShowSuggestions(false), 200);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSearch();
                            } else if (e.key === 'Escape') {
                              setShowSuggestions(false);
                            }
                          }}
                          placeholder="Adres ara (örn: Seyhan, Adana)"
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        />
                        <Icon
                          name="search"
                          size={18}
                          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        />
                        {isSearching && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </div>
                      
                      {showSuggestions && suggestions.length > 0 && (
                        <div
                          ref={suggestionsRef}
                          className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                        >
                          {suggestions.map((suggestion, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                handleAddressSelect(suggestion);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-primary/5 transition-colors border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-start gap-3">
                                <Icon
                                  name="location"
                                  size={18}
                                  className="text-primary flex-shrink-0 mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate">
                                    {suggestion.displayName.split(',')[0]}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                    {suggestion.displayName.split(',').slice(1).join(', ').trim()}
                                  </div>
                                  {suggestion.district && suggestion.city && (
                                    <div className="text-xs text-primary mt-1 font-medium">
                                      {suggestion.district}, {suggestion.city}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleSearch}
                      isLoading={isSearching}
                      disabled={!searchQuery.trim() || searchQuery.length < 3}
                    >
                      Ara
                    </Button>
                  </div>
                  {searchQuery && searchQuery.length < 3 && (
                    <p className="text-xs text-gray-500 mt-1 px-1">
                      En az 3 karakter girin
                    </p>
                  )}
                </div>
              </div>

              <div className="h-96 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 relative">
                {isMapReady && mapPosition ? (
                  <MapLibreMap
                    center={mapPosition}
                    zoom={13}
                    onMapLoad={handleMapLoad}
                    onMapClick={handleMapClick}
                    className="w-full h-full"
                  >
                    <MapMarker
                      position={mapPosition}
                      color="#E63946"
                      size={32}
                    />
                  </MapLibreMap>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    Harita yükleniyor...
                  </div>
                )}
                {mapPosition && (
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg text-xs">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      Seçilen Konum
                      {isReverseGeocoding && (
                        <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      )}
                    </div>
                    <div className="text-gray-600">
                      {mapPosition[1].toFixed(6)}, {mapPosition[0].toFixed(6)}
                    </div>
                    {searchQuery && (
                      <div className="text-gray-500 mt-1 text-xs truncate max-w-xs">
                        {searchQuery}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Adres Bilgileri
                </h3>
              </div>

              <Input
                label="Adres Başlığı *"
                placeholder="Örn: Ev, İş, Annem"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Select
                    label="Şehir *"
                    placeholder="Şehir seçin"
                    options={TURKISH_CITY_OPTIONS}
                    value={formData.city || undefined}
                    onChange={(value) =>
                      setFormData({ ...formData, city: (value as string) || '' })
                    }
                    searchable
                    required
                  />
                </div>
                <Input
                  label="İlçe *"
                  placeholder="Kadıköy"
                  value={formData.district}
                  onChange={(e) =>
                    setFormData({ ...formData, district: e.target.value })
                  }
                  required
                />
              </div>

              <Input
                label="Açık Adres *"
                placeholder="Sokak, cadde, mahalle adı ve numara"
                value={formData.fullAddress}
                onChange={(e) =>
                  setFormData({ ...formData, fullAddress: e.target.value })
                }
                required
              />

              <Input
                label="Bina Bilgisi (Opsiyonel)"
                placeholder="Örn: A Blok, Daire 5"
                value={formData.buildingInfo || ''}
                onChange={(e) =>
                  setFormData({ ...formData, buildingInfo: e.target.value })
                }
              />

              <label className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) =>
                    setFormData({ ...formData, isDefault: e.target.checked })
                  }
                  className="w-4 h-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  Bu adresi varsayılan adres olarak ayarla
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={isSubmitting}
              className="flex-1"
            >
              {isEditMode ? 'Güncelle' : 'Adresi Kaydet'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
